/**
 * The do-not-scan list on the Skannid tab of Kliendiotsing (`/prospects/scans`):
 * websites whose owner asked WebAdmin to stop checking them. No autopilot,
 * pasted list, brain run, lead watch or AI alert call touches a listed site,
 * and a staff member's single scan of one is refused with a message.
 *
 * A HAND-WRITTEN MIRROR of `apps/api/src/modules/ariregister/scan-exclusions.routes.ts`
 * (paths, `ExclusionView`) and `.../lib/scanner/exclusions/store.ts`
 * (`EXCLUSION_REASON_MAX`).
 *
 * React-free: the error reader is the part worth testing.
 */
import { ApiError, api } from "./api";

export const SCAN_EXCLUSIONS_PATH = "/admin/ariregister/scan-exclusions";

/** Mirrors `EXCLUSION_REASON_MAX`: the route refuses a longer reason. */
export const EXCLUSION_REASON_MAX = 500;

/** The code every refused scan answers with when its site is on the list. */
export const SITE_EXCLUDED = "site_excluded";

export interface ScanExclusion {
  /** The registrable domain the entry is stored under. */
  domain: string;
  reason: string | null;
  addedBy: string | null;
  /** The name now, or null when it cannot be found. */
  addedByName: string | null;
  /** ISO. */
  createdAt: string;
}

export interface AddExclusionResult {
  ok: true;
  /** False when the site was already on the list; `exclusion` is then the first entry. */
  created: boolean;
  exclusion: ScanExclusion;
}

export const listScanExclusions = (): Promise<{ exclusions: ScanExclusion[] }> =>
  api.get<{ exclusions: ScanExclusion[] }>(SCAN_EXCLUSIONS_PATH);

export const addScanExclusion = (domain: string, reason: string): Promise<AddExclusionResult> =>
  api.post<AddExclusionResult>(SCAN_EXCLUSIONS_PATH, { domain: domain.trim(), reason: reason.trim() || null });

export const removeScanExclusion = (domain: string): Promise<{ ok: boolean }> =>
  api.del<{ ok: boolean }>(`${SCAN_EXCLUSIONS_PATH}/${encodeURIComponent(domain)}`);

/** A failed add or remove, as a key under `leads.exclusions.error`. */
export function exclusionErrorKey(err: unknown): "invalidDomain" | "forbidden" | "failed" {
  if (!(err instanceof ApiError)) return "failed";
  if (err.status === 403) return "forbidden";
  const body = (err.body ?? {}) as { error?: unknown };
  if (body.error === "invalid_domain" || body.error === "bad_request") return "invalidDomain";
  return "failed";
}

/** A refused scan or run whose site is on the list. */
export function isSiteExcluded(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  return (err.body as { error?: unknown } | null)?.error === SITE_EXCLUDED;
}
